import { DynamicContainer, Form, FormSection } from '@hai/ui-react';
import { Formik } from 'formik';
import { FormikErrors } from 'formik/dist/types';
import { isNil } from 'ramda';
import React, { ReactNode, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiClientWithoutHandler } from 'src/js/api';
import { APIPath } from 'src/js/api/route-path-index';
import { FormValue } from 'src/js/component/base/form/form-value';
import { FormikTextfield } from 'src/js/component/base/form/formik-textfield';
import { useMutable } from 'src/js/hook/use-mutable';
import { IconStatusType } from 'src/js/model/icon-status-type';
import { CancelPairingButton } from 'src/js/pages/settings/services/cancel-pairing-button';
import { PairButton } from 'src/js/pages/settings/services/pair-button';
import { UnpairButton } from 'src/js/pages/settings/services/unpair-button';
import { FormChangeEvent } from 'src/js/util/global-type';
import { isNilOrEmpty, toTitleCase } from 'src/js/util/global-util';

//import { Loading } from 'src/js/component/loading';
import {
  EmsServiceResponse,
  EmsServiceState,
  emsValidationSchema,
  getEmsStateIcon,
  mapEmsServices,
} from './ems-models';
import emsService, { saveResponseToEmsService } from './ems-store';
import { ServicesViewModel } from './services-view-model';
import { EmsDecodeResponse } from 'src/js/model/api/response/ems-decode-response';

interface Props {
  services: ServicesViewModel /* loading animation could be displayed while services.nic?.ems === undefined */;
}

export const EmsForm: React.FunctionComponent<Props> = () => {
  const { t } = useTranslation();
  const pollingInterval = 5000;
  const pairingPollingInterval = 300;
  const [passcodeChangeTimeoutId, setPasscodeChangeTimeoutId] = useMutable(undefined);
  const [firstTime, setFirstTime] = useMutable(true); // we lock in the form param on first load from rest api values. Then they stay stable and not re-writen after the pairing code is decoded and pre-fills the form or if the user modify it. Exception is when we detect a state change from the REST API.
  const [skipNextGet, setSkipNextGet] = useMutable(false);
  const [lastPairingError, setLastPairingError] = useState('');
  const [pollingTimeout, setPollingTimeout] = useMutable(pollingInterval);
  const [emsFetchTimeoutId, setEmsFetchTimeoutId] = useMutable(null);
  const [emsState, setEmsState] = useState<EmsServiceState>(undefined);
  const [iconState, setIconState] = useState(getEmsStateIcon(emsState));
  const [underDecoding, setUnderDecoding] = useState(false);
  const formRef = useRef(null);

  let endrun = false;
  useEffect(() => {
    return () => {
      endrun = true;
    };
  }, []);

  // checks if the device is currently pairing/unpairing and adjust the polling timeout if needed
  const pairingCheck = (state: EmsServiceState): void => {
    if (
      (state === EmsServiceState.PAIRING || state === EmsServiceState.UNPAIRING) &&
      pollingTimeout() !== pairingPollingInterval
    ) {
      setPollingTimeout(pairingPollingInterval);
      if (emsFetchTimeoutId() !== null) {
        // console.log('clearing timeout to trigger right away a new fetch')
        clearTimeout(emsFetchTimeoutId());
        setEmsFetchTimeoutId(null);
      }
    }
    if (
      (state === EmsServiceState.CONNECTED || state === EmsServiceState.UNPAIRED) &&
      pollingTimeout() !== pollingInterval
    ) {
      setPollingTimeout(pollingInterval);
    }
  };

  const fetchStatusFunc = () => {
    if (endrun) {
      // console.error('count/endrun', count, endrun);
      return; // stop
    }
    apiClientWithoutHandler.genericController.get(APIPath.services.ems).then((res: any) => {
      // console.log('CALLBACK fetch, uuid', uuid, endrun);
      if (endrun) {
        // console.error('CALLBACK fetch, all instances stopped, aborting', uuid);
        // we got destroyed, abort
        return;
      }
      if (skipNextGet() === true) {
        setSkipNextGet(false);
        const timeoutVal = pollingTimeout();
        setEmsFetchTimeoutId(setTimeout(fetchStatusFunc, timeoutVal));
        return;
      }
      const data = res?.data?.data;
      if (res?.ok) {
        const response = mapEmsServices(res.data?.data as EmsServiceResponse);

        const state = data.state as EmsServiceState;
        if (emsService.state !== state) {
          // upstream state changes
          setFirstTime(true); // NKE-4062
        }

        saveResponseToEmsService(response);

        setEmsState(state);
        // console.log('setEmsState', state);
        setIconState(response.iconState);
        pairingCheck(response.state);
        if (formRef.current && response.state === EmsServiceState.CONNECTED) {
          // save actual keep alive from server. Usually it's 0.
          formRef.current.setFieldValue('keepAlive', response.emsKeepalive, false);
        }

        if (formRef.current) {
          if (firstTime()) {
            // it's needed to check firstTime() here to not wipe out a hostname and port after they have been filled by a decode
            setFirstTime(false);
            formRef.current.setFieldValue('hostname', response.emsHostname, false);
            if (response.emsPort !== 0) {
              formRef.current.setFieldValue('port', response.emsPort, false);
            }
            formRef.current.setFieldValue('keepAlive', response.emsKeepalive, false);
          }
        }
      }

      if (res?.ok && data?.lastPairingError && data?.lastPairingError.length) {
        // only valid for these states
        const state = data.state;
        if (
          state === 'CONNECTING' ||
          state === 'PAIRING' ||
          state === 'PAIRED' ||
          state === 'UNKNOWN' ||
          state === 'UNPAIRED'
        ) {
          /*
          // if we want notifications
          const errPrefix = 'Error ';
          if (!notificationShown) {
            notificationShown = true;

            if (data.lastPairingError.startsWith(errPrefix)) {
              createAndDispatchNotification(
                data.lastPairingError.substr(errPrefix.length),
                NotificationVariant.ERROR,
                t,
              );
            } else {
              createAndDispatchNotification(data.lastPairingError, NotificationVariant.RED, t);
            }

          }*/

          // for static red error text
          setLastPairingError(data.lastPairingError);

          if (state === 'UNPAIRED' && data.lastPairingError?.length) {
            // console.log('pairing ENDED', emsState);
            if (emsState !== state) {
              // fixing state
              setEmsState(EmsServiceState.UNPAIRED);
              // console.log('setEmsState UNPAIRED', state);
            }
          }

          if (data.lastPairingError === '') {
            setLastPairingError('');
          }
        } else if (lastPairingError !== '') {
          setLastPairingError('');
        }
      }

      if (!endrun) {
        const timeoutVal = pollingTimeout();
        // console.log('renewing fetch', timeoutVal);
        setEmsFetchTimeoutId(setTimeout(fetchStatusFunc, timeoutVal));
      } else {
        // console.error('not renewing fetch')
      }
    });
  };

  // just run once or when changing interval
  useEffect(() => {
    if (emsFetchTimeoutId() === null) {
      // console.log('triggerFetch because we have null ID')
      setEmsFetchTimeoutId(setTimeout(fetchStatusFunc, 0));
    }
  }, [emsFetchTimeoutId]);

  /**
   * Because we use the pair buttons to validate, we set the field to touched before so the errors
   * can be displayed. This can be removed if we ever get back to an architecture where the pair buttons
   * are submitting the forms.
   */
  const handleValidate = (): Promise<FormikErrors<any>> => {
    formRef.current.setTouched({ hostname: true, keepAlive: true, pairingCode: true, port: true }, false);
    return formRef.current.validateForm();
  };

  const clearPasscodeChangeTimeout = (): void => {
    if (!isNil(passcodeChangeTimeoutId())) {
      clearTimeout(passcodeChangeTimeoutId());
      setPasscodeChangeTimeoutId(undefined);
    }
  };

  const [hostnamePlaceholder, setHostnamePlaceholder] = useState<string>();
  const [portPlaceholder, setPortPlaceholder] = useState<string>();
  const [keepAlivePlaceholder, setKeepAlivePlaceholder] = useState<string>();
  const handlePasscodeChange = (pairingCode: string) => {
    if (!isNilOrEmpty(pairingCode)) {
      const path = APIPath.ems.decode;
      setUnderDecoding(true);
      return apiClientWithoutHandler.genericController
        .put<EmsDecodeResponse>(path, { pairingCode: pairingCode })
        .then((res) => {
          const response = res?.data?.data;
          if (response) {
            formRef.current.setFieldValue('hostname', response.emsHostname, true);
            setHostnamePlaceholder(response.emsHostname);
            formRef.current.setFieldValue('port', response.emsPort, true);
            setPortPlaceholder(response.emsPort.toString());
            formRef.current.setFieldValue('keepAlive', response.emsKeepalive, true);
            setKeepAlivePlaceholder(response.emsKeepalive.toString());
          }
          return res;
        })
        .finally(() => {
          setUnderDecoding(false);
        });
    }
    return null;
  };

  const onOperationStarted = (emsState: EmsServiceState): void => {
    setSkipNextGet(true);
    setLastPairingError('');
    setEmsState(emsState);
    setIconState(IconStatusType.STARTING);
    pairingCheck(emsState);
    // console.log('pairingStarted', emsState);
  };

  // TODO use the same field names as the REST API names in the form for simplification
  const renderPairingButton = (values: any): ReactNode => {
    switch (emsState) {
      case EmsServiceState.PAIRING:
      case EmsServiceState.PAIRED:
      case EmsServiceState.CONNECTING:
        return <CancelPairingButton onOperationStarted={() => onOperationStarted(EmsServiceState.PAIRING)} />;
      case EmsServiceState.UNPAIRED:
        return (
          <PairButton
            validate={handleValidate}
            onOperationStarted={() => onOperationStarted(EmsServiceState.PAIRING)}
            pairingCode={values.pairingCode}
            hostname={values.hostname}
            busy={underDecoding}
            port={typeof values.port !== 'number' || isNaN(values.port) ? 0 : values.port}
            keepAlive={typeof values.keepAlive !== 'number' || isNaN(values.keepAlive) ? '' : values.keepAlive}
          />
        );
      case EmsServiceState.CONNECTED:
      case EmsServiceState.UNPAIRING:
        return (
          <UnpairButton
            onOperationStarted={() => {
              onOperationStarted(EmsServiceState.UNPAIRING);
              setPortPlaceholder(undefined);
              setHostnamePlaceholder(undefined);
              setKeepAlivePlaceholder(undefined);
              // TODO move those to after the unpair has completed
              formRef.current.setFieldValue('pairingCode', '');
              formRef.current.setFieldTouched('pairingCode', false);
              formRef.current.setFieldValue('port', '');
              formRef.current.setFieldTouched('port', false);
              formRef.current.setFieldValue('hostname', '');
              formRef.current.setFieldTouched('hostname', false);
              formRef.current.setFieldValue('keepAlive', ' ');
              formRef.current.setFieldTouched('keepAlive', false);
            }}
            disabled={emsState === EmsServiceState.UNPAIRING}
          />
        );
      default:
        return null;
    }
  };

  const loaded = !firstTime();

  return (
    <Formik
      className="hai-mb-6"
      initialValues={{
        keepAlive: ' ',
        pairingCode: '',
        hostname: '',
        port: '',
      }}
      onSubmit={() => {
        //
      }}
      innerRef={formRef}
      validationSchema={emsValidationSchema(t)}
    >
      {(formikProps) => {
        if (formikProps.values.pairingCode?.length > 0 && emsState == EmsServiceState.CONNECTED) {
          // clear pairingCode R4KD-2871
          formikProps.setFieldValue('pairingCode', '');
          formikProps.setFieldTouched('pairingCode', false);
          //   formikProps.setFieldValue('port', '');
          //   formikProps.setFieldTouched('port', false);
          //   formikProps.setFieldValue('hostname', '');
          //   formikProps.setFieldTouched('hostname', false);
          // formikProps.setFieldValue('keepAlive', '');
          //  formikProps.setFieldTouched('keepAlive', false);
        }
        return (
          <Form className="hai-mb-6">
            <FormSection title={t('settings.services.section.ems.title')}>
              <DynamicContainer minColumns={3}>
                <FormValue
                  label={t('settings.services.section.ems.connectionStatus.label')}
                  value={toTitleCase(emsState)}
                  icon={iconState}
                />
                <div />
                <div className="d-flex ml-auto">{renderPairingButton(formikProps.values)}</div>
              </DynamicContainer>
              <DynamicContainer minColumns={1}>
                {loaded && emsState !== EmsServiceState.UNPAIRED ? (
                  <FormValue label={t('settings.services.section.ems.passcode.label')} value={'•••••••••••••••••••'} />
                ) : (
                  <FormikTextfield
                    name="pairingCode"
                    mask
                    title={t('settings.services.section.ems.passcode.label')}
                    placeholder={t('settings.services.section.ems.passcode.labelExplain')}
                    onChange={(event: FormChangeEvent) => {
                      const passcode = event.target.value;
                      clearPasscodeChangeTimeout();
                      setPasscodeChangeTimeoutId(
                        setTimeout(() => {
                          handlePasscodeChange(passcode);
                        }, 500),
                      );
                    }}
                    onFocus={(e) => {
                      // select all on focus
                      (e.target as HTMLInputElement).select();
                    }}
                    required
                  />
                )}
              </DynamicContainer>
              <DynamicContainer minColumns={3}>
                {emsState !== EmsServiceState.UNPAIRED ? (
                  <>
                    <FormValue
                      label={t('settings.services.section.ems.hostname.label')}
                      value={
                        formikProps.values.hostname && formikProps.values.hostname.length > 0
                          ? formikProps.values.hostname
                          : hostnamePlaceholder ?? formikProps.values.hostname
                      }
                    />
                    <FormValue
                      label={t('settings.services.section.ems.port.label')}
                      value={
                        (formikProps.values.port && formikProps.values.port.length > 0) ||
                        (formikProps.values.port as unknown as number) > 0
                          ? formikProps.values.port
                          : portPlaceholder
                      }
                    />
                    <FormValue
                      label={t('settings.services.section.ems.keepAlive.label')}
                      value={
                        formikProps.values.keepAlive === ' '
                          ? ''
                          : Number(formikProps.values.keepAlive) > 0
                          ? formikProps.values.keepAlive.toString()
                          : '5'
                      }
                    />
                  </>
                ) : (
                  <>
                    <FormikTextfield
                      name="hostname"
                      mask
                      title={t('settings.services.section.ems.hostname.label')}
                      placeholder={hostnamePlaceholder} // ?? t('settings.services.section.ems.hostname.labelExplain')}
                      disabled={formikProps.values.pairingCode === ''}
                      required
                    />
                    <FormikTextfield
                      name="port"
                      mask
                      title={t('settings.services.section.ems.port.label')}
                      placeholder={portPlaceholder} // ?? t('settings.services.section.ems.port.labelExplain')}
                      type="number"
                      disabled={formikProps.values.pairingCode === ''}
                      required
                    />
                    <FormikTextfield
                      name="keepAlive"
                      title={t('settings.services.section.ems.keepAlive.label')}
                      placeholder={
                        keepAlivePlaceholder && Number(keepAlivePlaceholder) > 0
                          ? keepAlivePlaceholder
                          : formikProps.values.pairingCode === ''
                          ? ''
                          : '' // t('general.auto')
                      }
                      disabled={formikProps.values.pairingCode === ''}
                      type="number"
                    />
                  </>
                )}
              </DynamicContainer>
              {lastPairingError && (
                <div>
                  <span className="error">{lastPairingError}</span>
                </div>
              )}
            </FormSection>
          </Form>
        );
      }}
    </Formik>
  );
};
